<template>
  <div>
    <v-dialog
      v-model="showModal"
      fullscreen
      persistent
      transition="dialog-bottom-transition"
      scrollable
    >
      <v-card style="display: flex; flex-direction: column; height: 100vh; overflow: hidden;">
        <!-- Toolbar -->
        <v-toolbar dark color="primary" dense style="flex-shrink: 0;">
          <v-btn icon dark @click="closeModal">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>Vista de Planos y Entregas</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn text @click="closeModal">Cerrar</v-btn>
        </v-toolbar>

        <div style="flex: 1; overflow: hidden; display: flex; flex-direction: column; min-height: 0;">
          <!-- Plano Selector -->
          <div class="px-4 pt-3 pb-2" style="flex-shrink: 0;">
            <v-select
              v-model="selectedPlano"
              :items="planos"
              item-text="title"
              item-value="id"
              label="Seleccione el Plano"
              prepend-icon="mdi-file-document-outline"
              outlined
              dense
              hide-details
              @change="loadPlanoEntregas"
            ></v-select>
          </div>

          <!-- Viewer area -->
          <div style="flex: 1; overflow: hidden; display: flex; min-height: 0;">
            <!-- Canvas / Forge viewer (izquierda) -->
            <div style="flex: 1; overflow: hidden; position: relative;">
              <div v-if="!selectedPlano" class="ptv-placeholder">
                <v-icon size="56" color="grey lighten-1">mdi-image-outline</v-icon>
                <div class="text-subtitle-2 mt-3 grey--text">Seleccione un plano para ver sus entregas</div>
              </div>
              <template v-else>
                <!-- Forge viewer -->
                <ForgePolygonViewer
                  v-if="selectedPlanoIsForge"
                  :read-only="true"
                  :all-polygons="allPolygons"
                  :forge-file-urn="selectedForgeFileUrn"
                  style="height: 100%;"
                />
                <!-- Canvas viewer -->
                <ImagePolygonViewer
                  v-else
                  :plano="selectedPlanoObj"
                  :read-only="true"
                  :all-polygons="allPolygons"
                  style="height: 100%;"
                />
              </template>
            </div>

            <!-- Entregas sidebar (derecha) -->
            <div style="width: 260px; flex-shrink: 0; border-left: 1px solid #e0e0e0; background: #fff; display: flex; flex-direction: column; overflow: hidden;">
              <div class="px-3 py-2 d-flex align-center" style="border-bottom: 1px solid #f0f0f0; background: #fafafa; flex-shrink: 0;">
                <v-icon small color="primary" class="mr-2">mdi-package-variant-closed</v-icon>
                <span class="text-body-2 font-weight-medium">Entregas Asociadas</span>
              </div>

              <div style="flex: 1; overflow-y: auto;" class="pa-2">
                <div v-if="!selectedPlano" class="text-center pa-4 text-caption grey--text">
                  Seleccione un plano
                </div>
                <v-skeleton-loader v-else-if="loadingEntregas" type="list-item-two-line, list-item-two-line" />
                <template v-else-if="entregasForSelectedPlano.length > 0">
                  <v-list dense class="pa-0">
                    <v-list-item
                      v-for="(item, index) in entregasForSelectedPlano"
                      :key="index"
                      class="py-1"
                    >
                      <v-list-item-avatar size="12" :color="item.color || '#2D4B95'" class="my-0 mr-2" style="min-width: 12px;"></v-list-item-avatar>
                      <v-list-item-content class="py-0">
                        <v-list-item-title class="text-body-2">{{ item.entrega ? item.entrega.title : 'Entrega' }}</v-list-item-title>
                        <v-list-item-subtitle v-if="item.entrega" class="text-caption">
                          <v-chip x-small :style="{ backgroundColor: item.entrega.status_info.color, color: 'white' }">
                            {{ item.entrega.status_info.text }}
                          </v-chip>
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </template>
                <div v-else class="text-center pa-4 text-caption grey--text">
                  No hay entregas asociadas a este plano
                </div>
              </div>
            </div>
          </div>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { getPlanos, getPlano } from '@/helpers/api/entrega'
import ForgePolygonViewer from './ForgePolygonViewer.vue'
import ImagePolygonViewer from './ImagePolygonViewer.vue'

export default {
  name: 'PlanoTotalViewer',

  components: {
    ForgePolygonViewer,
    ImagePolygonViewer
  },

  data () {
    return {
      showModal: false,
      planos: [],
      selectedPlano: null,
      loadingEntregas: false,
      entregasForSelectedPlano: []
    }
  },

  computed: {
    modalState () {
      return this.$store?.entregaStore?.state?.planoTotalViewerModal || false
    },

    /** Plano seleccionado como objeto */
    selectedPlanoObj () {
      return this.planos.find(p => p.id === this.selectedPlano) || null
    },

    /** El plano seleccionado es de tipo Forge */
    selectedPlanoIsForge () {
      const plano = this.planos.find(p => p.id === this.selectedPlano)
      return !!(plano && (
        (plano.file && plano.file.forge_file && plano.file.forge_file.urn) ||
        (plano.forge_file && plano.forge_file.urn)
      ))
    },

    /** URN del modelo Forge del plano seleccionado */
    selectedForgeFileUrn () {
      const plano = this.planos.find(p => p.id === this.selectedPlano)
      return plano?.file?.forge_file?.urn || plano?.forge_file?.urn || null
    },

    /** Polígonos unificados para ForgePolygonViewer (allPolygons) e ImagePolygonViewer (allPolygons) */
    allPolygons () {
      return this.entregasForSelectedPlano
        .filter(item => item.nodes && item.nodes.length > 0)
        .map(item => ({
          nodes:   item.nodes,
          color:   item.color   || '#2D4B95',
          opacity: item.opacity != null ? item.opacity : 0.3
        }))
    }
  },

  watch: {
    modalState: {
      immediate: true,
      handler (newVal) {
        this.showModal = newVal
        if (newVal) {
          this.$nextTick(() => this.loadPlanos())
        }
      }
    }
  },

  methods: {
    closeModal () {
      this.showModal = false
      this.$store.entregaStore.commit('setPlanoTotalViewerModal', false)
    },

    async loadPlanos () {
      const projectId = this.$store?.projectsStore?.state?.selectedProject?.id
      if (!projectId) return
      try {
        const response = await getPlanos({ project_id: projectId, start: 0, limit: 100 })
        if (response && response.code === 200 && response.planos) {
          this.planos = response.planos
          if (this.planos.length > 0 && !this.selectedPlano) {
            this.selectedPlano = this.planos[0].id
            this.loadPlanoEntregas(this.selectedPlano)
          }
        }
      } catch (e) {
        console.error('[PlanoTotalViewer] Error loading planos:', e)
      }
    },

    async loadPlanoEntregas (planoId) {
      if (!planoId) return
      this.loadingEntregas           = true
      this.entregasForSelectedPlano  = []
      try {
        const response = await getPlano({ plano_id: planoId })
        if (response && Array.isArray(response.planos)) {
          this.entregasForSelectedPlano = response.planos
        }
      } catch (e) {
        console.error('[PlanoTotalViewer] Error loading entregas:', e)
      } finally {
        this.loadingEntregas = false
      }
    },


  }
}
</script>

<style scoped>
.ptv-placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
}
</style>
